/* ============================================================== */
/*   ไฟล์เอกสารสแกนของหน้า Document (browser only)                  */
/* ============================================================== */
/*
 * แยกจาก documentModelV4 ที่ต้อง pure เพราะไฟล์นี้ใช้ File/canvas/pdfjs
 *
 * ทำตาม V3 (HISV3/common/CardScanner.tsx, CardScannedDocument.tsx):
 *   document_image เป็น Base64ImageField ที่รับเฉพาะรูป จึงแปลง PDF เป็น JPEG หน้าละ 1 ภาพ
 *   และรับเฉพาะ JPEG เพราะการรวมหน้าเป็น PDF ของ V3 ใช้ embedJpg
 */
import { printURL } from "react-lib/utils/printURL";

import { MAX_SCAN_IMAGE_BASE64_LENGTH, type ScanPage } from "./documentModelV4";

const JPEG_BASE64_PREFIX = "/9j/";
const PDF_RENDER_SCALE = 2;
const PDF_JPEG_QUALITY = 0.92;

export type ReadScanResult = {
  pages: ScanPage[];
  /** ชื่อไฟล์ที่อ่านไม่ได้หรือชนิดไม่รองรับ */
  rejected: string[];
};

const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^.*,/, ""));
    reader.onerror = () => reject(new Error(`อ่านไฟล์ไม่สำเร็จ: ${file.name}`));
    reader.readAsDataURL(file);
  });

const pdfToPages = async (file: File): Promise<ScanPage[]> => {
  const { pdfjs } = await import("react-pdf");
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

  const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const baseName = file.name.replace(/\.pdf$/i, "");
  const pages: ScanPage[] = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const context = canvas.getContext("2d", { alpha: false });
    if (context === null) throw new Error(`แปลงไฟล์ไม่สำเร็จ: ${file.name}`);

    await page.render({ canvasContext: context, viewport }).promise;
    pages.push({
      name: `${baseName} (${pageNumber}).jpg`,
      image: canvas.toDataURL("image/jpeg", PDF_JPEG_QUALITY).replace(/^.*,/, ""),
    });
    canvas.remove();
  }

  await pdf.destroy();
  return pages;
};

const isPdf = (file: File): boolean =>
  file.type === "application/pdf" || /\.pdf$/i.test(file.name);

/** อ่านไฟล์ที่ผู้ใช้เลือกเป็นภาพ base64 หน้าละ 1 รายการ เรียงตามลำดับที่เลือก */
export const readScanFiles = async (
  files: File[],
  maxBase64Length: number = MAX_SCAN_IMAGE_BASE64_LENGTH,
): Promise<ReadScanResult> => {
  const pages: ScanPage[] = [];
  const rejected: string[] = [];

  for (const file of files) {
    try {
      const filePages = isPdf(file)
        ? await pdfToPages(file)
        : [{ name: file.name, image: await readAsBase64(file) }];
      const valid = filePages.every(
        (page) =>
          page.image.startsWith(JPEG_BASE64_PREFIX) && page.image.length <= maxBase64Length,
      );
      if (valid) pages.push(...filePages);
      else rejected.push(file.name);
    } catch {
      rejected.push(file.name);
    }
  }

  return { pages, rejected };
};

/** รวมภาพทุกหน้าเป็น PDF ไฟล์เดียวแบบ generateDocumentImage ของ V3 คืน blob URL */
const buildScanPdfUrl = async (urls: string[]): Promise<string> => {
  const { PDFDocument } = await import("pdf-lib");
  const pdf = await PDFDocument.create();

  for (const url of urls) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`โหลดภาพเอกสารไม่สำเร็จ (${response.status})`);
    const bytes = await response.arrayBuffer();
    const isJpeg = new Uint8Array(bytes.slice(0, 2)).every((value, i) => value === [0xff, 0xd8][i]);
    const image = isJpeg ? await pdf.embedJpg(bytes) : await pdf.embedPng(bytes);
    const size = image.scale(0.5);
    pdf.addPage([size.width, size.height]).drawImage(image, {
      x: 0,
      y: 0,
      width: size.width,
      height: size.height,
    });
  }

  const blob = new Blob([await pdf.save()], { type: "application/pdf" });
  return URL.createObjectURL(blob);
};

export const printScanPages = async (urls: string[]): Promise<void> => {
  printURL(await buildScanPdfUrl(urls));
};

export const downloadScanPages = async (urls: string[], name: string): Promise<void> => {
  const url = await buildScanPdfUrl(urls);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${name}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
